'use client';

import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, ShoppingCart } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';
import { toast } from 'sonner';

export type PO = {
  id: string;
  poNumber: string;
  jobId: string;
  jobName: string;
  deliveryRef: string;
  supplier: string;
  createdDate: string;
  total: string;
  totalRaw: number;
  status: string;
  linkedSo: string;
  linkedInvoice: string;
};

export const poData: PO[] = [
  { id: 'po-2854', poNumber: 'PO-2854', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', deliveryRef: 'DEL-9924-04', supplier: 'HydroFlask Trade', createdDate: '16/08/2026', total: '£11,000', totalRaw: 11000, status: 'draft', linkedSo: 'SO-3244', linkedInvoice: '—' },
  { id: 'po-2853', poNumber: 'PO-2853', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', deliveryRef: 'DEL-9924-03', supplier: 'PrintFast UK', createdDate: '15/08/2026', total: '£6,800', totalRaw: 6800, status: 'received', linkedSo: 'SO-3243', linkedInvoice: 'INV-10479' },
  { id: 'po-2852', poNumber: 'PO-2852', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', deliveryRef: 'DEL-9924-02', supplier: 'EcoPromo Supplies', createdDate: '15/08/2026', total: '£8,200', totalRaw: 8200, status: 'sent', linkedSo: 'SO-3242', linkedInvoice: '—' },
  { id: 'po-2851', poNumber: 'PO-2851', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', deliveryRef: 'DEL-9924-01', supplier: 'Gildan Activewear EU', createdDate: '15/08/2026', total: '£12,400', totalRaw: 12400, status: 'acknowledged', linkedSo: 'SO-3241', linkedInvoice: '—' },
  { id: 'po-2848', poNumber: 'PO-2848', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', deliveryRef: 'DEL-9921-02', supplier: 'Gildan Activewear EU', createdDate: '13/08/2026', total: '£9,600', totalRaw: 9600, status: 'sent', linkedSo: 'SO-3239', linkedInvoice: '—' },
  { id: 'po-2847', poNumber: 'PO-2847', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', deliveryRef: 'DEL-9921-01', supplier: 'Gildan Activewear EU', createdDate: '13/08/2026', total: '£13,200', totalRaw: 13200, status: 'acknowledged', linkedSo: 'SO-3238', linkedInvoice: '—' },
  { id: 'po-2843', poNumber: 'PO-2843', jobId: 'JOB-CRM-9919', jobName: 'Promo kit — SaaS Summit', deliveryRef: 'DEL-9919-01', supplier: 'Moleskine Trade', createdDate: '11/08/2026', total: '£9,400', totalRaw: 9400, status: 'acknowledged', linkedSo: 'SO-3231', linkedInvoice: '—' },
  { id: 'po-2842', poNumber: 'PO-2842', jobId: 'JOB-CRM-9919', jobName: 'Promo kit — SaaS Summit', deliveryRef: 'DEL-9919-02', supplier: 'PrintFast UK', createdDate: '11/08/2026', total: '£7,200', totalRaw: 7200, status: 'sent', linkedSo: 'SO-3232', linkedInvoice: '—' },
  { id: 'po-2838', poNumber: 'PO-2838', jobId: 'JOB-CRM-9917', jobName: 'Tote bags — GreenFest', deliveryRef: 'DEL-9917-01', supplier: 'EcoPromo Supplies', createdDate: '08/08/2026', total: '£4,800', totalRaw: 4800, status: 'received', linkedSo: 'SO-3225', linkedInvoice: 'INV-10478' },
  { id: 'po-2835', poNumber: 'PO-2835', jobId: 'JOB-CRM-9915', jobName: 'Embroidered polos — HealthOrg', deliveryRef: 'DEL-9915-01', supplier: 'Gildan Activewear EU', createdDate: '05/08/2026', total: '£14,200', totalRaw: 14200, status: 'closed', linkedSo: 'SO-3218', linkedInvoice: 'INV-10475' },
  { id: 'po-2831', poNumber: 'PO-2831', jobId: 'JOB-CRM-9912', jobName: 'Caps and beanies — SportsBrand', deliveryRef: 'DEL-9912-01', supplier: 'Headwear Direct', createdDate: '01/08/2026', total: '£7,800', totalRaw: 7800, status: 'closed', linkedSo: 'SO-3211', linkedInvoice: 'INV-10471' },
];

const statusOptions = ['All statuses', 'Draft', 'Sent', 'Acknowledged', 'Received', 'Closed'];
const supplierOptions = ['All suppliers', 'Gildan Activewear EU', 'PrintFast UK', 'EcoPromo Supplies', 'HydroFlask Trade', 'Moleskine Trade', 'Headwear Direct'];

export default function POContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [supplierFilter, setSupplierFilter] = useState('All suppliers');

  const filtered = useMemo(() => {
    return poData.filter(po => {
      const matchSearch = !search ||
        po.poNumber.toLowerCase().includes(search.toLowerCase()) ||
        po.jobName.toLowerCase().includes(search.toLowerCase()) ||
        po.supplier.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All statuses' || po.status === statusFilter.toLowerCase();
      const matchSupplier = supplierFilter === 'All suppliers' || po.supplier === supplierFilter;
      return matchSearch && matchStatus && matchSupplier;
    });
  }, [search, statusFilter, supplierFilter]);

  const handleCreatePO = () => {
    toast.info('Create PO — connect to .NET API endpoint POST /api/purchase-orders');
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>Purchase orders</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
            {poData.length} total — {poData.filter(p => p.status === 'sent').length} sent awaiting acknowledgement
          </p>
        </div>
        <button
          onClick={handleCreatePO}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md text-white transition-all active:scale-95"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          <Plus size={15} />
          Create PO
        </button>
      </div>

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3 p-4 rounded-xl border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div
          className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-md border"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
        >
          <Search size={14} style={{ color: 'var(--slate)' }} />
          <input
            type="text"
            placeholder="Search PO number, job, or supplier…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: 'var(--foreground)' }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--slate)' }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {statusOptions.map(s => <option key={`po-status-opt-${s}`}>{s}</option>)}
            </select>
          </div>
          <select
            value={supplierFilter}
            onChange={e => setSupplierFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)', maxWidth: '200px' }}
          >
            {supplierOptions.map(s => <option key={`supplier-opt-${s}`}>{s}</option>)}
          </select>
        </div>
        <span className="text-xs ml-auto" style={{ color: 'var(--slate)' }}>
          {filtered.length} of {poData.length} POs
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                {['PO number', 'Job', 'Delivery ref', 'Supplier', 'Created', 'Total', 'Linked SO', 'Status', ''].map((h, i) => (
                  <th
                    key={`po-th-${i}`}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <ShoppingCart size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No purchase orders match your filters</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>POs are generated automatically per delivery on each job</p>
                  </td>
                </tr>
              ) : (
                filtered.map(po => (
                  <tr
                    key={po.id}
                    className="row-hover border-b"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <td className="px-4 py-3">
                      <Link href={`/purchase-orders/${po.id}`} className="text-xs font-mono font-semibold hover:underline" style={{ color: 'var(--accent)' }}>
                        {po.poNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{po.jobId}</p>
                      <p className="text-sm truncate font-medium" style={{ color: 'var(--foreground)' }}>{po.jobName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{po.deliveryRef}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--foreground)' }}>{po.supplier}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--slate)' }}>{po.createdDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>{po.total}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono" style={{ color: po.linkedSo !== '—' ? 'var(--accent)' : 'var(--slate)' }}>{po.linkedSo}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={po.status as never} />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/purchase-orders/${po.id}`}
                        className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors hover:bg-muted whitespace-nowrap"
                        style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--slate)' }}>
            Showing {filtered.length} of {poData.length} purchase orders
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map(p => (
              <button
                key={`po-page-${p}`}
                className="w-7 h-7 text-xs rounded-md font-medium transition-colors"
                style={{
                  backgroundColor: p === 1 ? 'var(--ink)' : 'transparent',
                  color: p === 1 ? 'white' : 'var(--slate)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--slate)' }}>Rows per page:</span>
            <select
              className="text-xs px-2 py-1 rounded-md border outline-none"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {['15', '25', '50'].map(v => <option key={`po-rows-${v}`}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}